import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../__generated__/schema-lite';
import { useAuth } from '../auth/AuthContext';
import { translateError } from '../lib/translate-error';

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setToken } = useAuth();
  const crud = useClientQueries(schema);
  const accept = crud.$procs.acceptInvite.useMutation();
  const check = crud.$procs.checkInvite.useMutation();

  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [invitee, setInvitee] = useState<{
    email: string;
    name: string | null;
    // Set only for a delegate invite — whose calls the invitee is joining.
    accountLabel: string | null;
    // Already has a login. Accepting adds a scoped membership; the server now requires
    // this person to confirm with their existing password before it issues a session, so
    // this screen asks for that password (it is verified, never changed).
    userExists: boolean;
  } | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  // StrictMode double-mount guard — checkInvite is a write (sets visitedAt).
  const checkedRef = useRef(false);

  useEffect(() => {
    if (!token || checkedRef.current) return;
    checkedRef.current = true;
    check
      .mutateAsync({ args: { input: { token } } })
      .then((res) => {
        setInvitee({
          email: res.email,
          name: res.name ?? null,
          accountLabel: res.accountLabel ?? null,
          userExists: res.userExists === true,
        });
        if (res.name && !name) setName(res.name);
      })
      .catch((err) => {
        setTokenError(translateError(t, err));
      });
    // We only want this to run once per token mount, not on every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Two different screens behind one URL: setting up a login, or accepting access to
  // another line with the login you already have.
  const existingUser = invitee?.userExists === true;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) {
      toast.error(t('errors.INVITE_INVALID'));
      return;
    }
    try {
      const jwt = await accept.mutateAsync({
        args: {
          input: {
            token,
            // Sent in both cases now: a new user sets this password, an existing user
            // proves their identity with it (verified server-side, never changed).
            password,
            // Only a new user supplies a name; an existing person keeps theirs.
            name: existingUser ? undefined : name || undefined,
          },
        },
      });
      setToken(jwt);
      toast.success(t('accept.welcome'));
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  if (!token || tokenError) {
    return (
      <div className="mx-auto max-w-md px-4 py-12 sm:py-20 text-center space-y-4">
        <h1 className="text-2xl font-semibold">{t('accept.title')}</h1>
        <p className="text-muted-foreground">{tokenError ?? t('errors.INVITE_INVALID')}</p>
        <Link to="/login" className="text-primary hover:underline text-sm">
          {t('action.signIn')}
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-sm px-4 py-10 sm:py-16">
      <h1 className="text-2xl font-semibold mb-2">
        {existingUser ? t('accept.existingTitle') : t('accept.title')}
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        {existingUser ? t('accept.existingBody') : t('accept.body')}
      </p>
      {invitee ? (
        <>
          <p className="text-sm text-muted-foreground mb-4">
            {t('field.email')}:{' '}
            <code className="text-foreground bg-muted px-1 rounded">{invitee.email}</code>
          </p>
          {invitee.accountLabel ? (
            <p className="text-sm text-foreground mb-4">
              {t('accept.joining', { account: invitee.accountLabel })}
            </p>
          ) : null}
        </>
      ) : null}
      <form onSubmit={onSubmit} className="space-y-4">
        {existingUser ? (
          // An existing person confirms with the password they already have — it is
          // verified, never changed, so no minimum-length rule applies here.
          <Field label={t('field.password')}>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
            />
          </Field>
        ) : (
          <>
            <Field label={t('field.nameOptional')}>
              <input
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
              />
            </Field>
            <Field label={t('field.password')}>
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
              />
            </Field>
          </>
        )}
        <button type="submit" disabled={accept.isPending} className="btn-primary w-full">
          {accept.isPending
            ? t('accept.submitting')
            : existingUser
              ? t('accept.existingSubmit')
              : t('accept.submit')}
        </button>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
